import React, { useEffect, useMemo } from "react";
import {
  Divider,
  Drawer,
  Input,
  Modal,
  Popconfirm,
  PopconfirmProps,
  Tag,
} from "antd";
import { getGeoData } from "@/utils/apis/auth/signup.api";
import ListView from "../list";
import SubCollectionTagSkeleton from "../loader/skeletons/SubCollectionTagSkeleton";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import { loadingToast, successToast } from "../toasts";
import {
  createSubCollection,
  deleteSubCollection,
  editSubCollection,
} from "@/utils/apis/minerals/my-collection.api";
import LabelInputComponent from "../input/labledinput";
import { useAppContext } from "@/context";
import { SVG } from "@/assets/svg";

const { Search } = Input;

type Props = {
  open: boolean;
  onClose: () => void;
};

const SubCollectionDrawer = ({ open, onClose }: Props) => {
  const { user } = useAppContext();

  const [subCollections, setSubCollections] = React.useState<
    { name: string; _id: string }[]
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [createLoading, setCreateLoading] = React.useState(false);
  const [selectedSubCollectionId, setSelectedSubCollectionId] =
    React.useState("");
  const [subCollectionName, setSubCollectionName] = React.useState("");
  const [subCollectionText, setSubCollectionText] = React.useState("");
  const [editLoading, setEditLoading] = React.useState(false);

  const handleCreateSubCollection = async (name: string) => {
    try {
      setCreateLoading(true);
      const body = {
        name,
        addedBy: user?._id,
      };
      const response = await createSubCollection(body);
      if (response.data.success) {
        setSubCollections((prev) => [response.data.data, ...prev]);
        setSubCollectionName("");
        successToast("Sub Collection Added Successfully");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const fetchSubCollections = async () => {
    try {
      if (!open) return;
      console.log("fetching sub collections");
      setLoading(true);
      const response = await getGeoData<{ name: string; _id: string }[]>(
        "subCollection",
        {
          limit: 100,
          page: 1,
          addedBy: user?._id,
          sort: {
            key: "createdAt",
            order: "desc",
          },
        }
      );

      if (response.data.success) {
        setSubCollections(response.data.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubCollections();
  }, [open]);

  const handleDeleteSubCollection = async (id: string) => {
    try {
      setEditLoading(true);
      const response = await deleteSubCollection(id);
      if (response.data.success) {
        setSubCollections(
          subCollections.filter((subCollection) => subCollection._id !== id)
        );
        successToast("Sub Collection Deleted Successfully");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setEditLoading(false);
    }
  };

  const selectedSubCollection = useMemo(
    () =>
      subCollections.find(
        (subCollection) => subCollection._id === selectedSubCollectionId
      ),
    [selectedSubCollectionId, subCollections]
  );

  const handleUpdateSubCollection = async () => {
    try {
      loadingToast("Updating Sub Collection...");
      const response = await editSubCollection(
        selectedSubCollectionId,
        subCollectionText
      );

      if (response.data.success) {
        setSubCollections(
          subCollections.map((subCollection) =>
            subCollection._id === selectedSubCollectionId
              ? { ...subCollection, name: subCollectionText }
              : subCollection
          )
        );

        successToast("Sub Collection Updated Successfully");
        closeModal();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const closeModal = () => {
    setSelectedSubCollectionId("");
    setSubCollectionText("");
  };

  useEffect(() => {
    if (selectedSubCollection) {
      setSubCollectionText(selectedSubCollection?.name);
    }
  }, [selectedSubCollection]);

  return (
    <Drawer
      title="Manage Sub Collections "
      placement="left"
      closable
      onClose={onClose}
      open={open}
      className="subCollectionDrawer"
    >
      <div className="CollectionDrawer"></div>
      <div
        style={{
          padding: "1rem",
        }}
      >
        <label
          style={{
            marginBottom: "12px",
            display: "flex",
            color: "#557EFF",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          Add Sub Collection
        </label>
        <Search
          placeholder="Enter Title"
          enterButton="Add"
          // size="large"
          loading={createLoading}
          style={{ width: "100%" }}
          onSearch={(value) => {
            handleCreateSubCollection(value);
          }}
          onChange={(e) => setSubCollectionName(e.target.value)}
          value={subCollectionName}
        />

        <Divider
          plain
          style={{
            marginBlock: "12px",
            display: "flex",
            color: "#000",
            fontSize: "14px",
            fontWeight: "500",
          }}
          orientation="center"
        >
          Your Sub Collections
        </Divider>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
          }}
        >
          <ListView
            data={subCollections}
            loading={loading}
            loader={<SubCollectionTagSkeleton />}
            renderItem={(item) => (
              <div
                style={{
                  padding: "0.4rem",
                  borderRadius: "10px",
                  //   marginBottom: "0.5rem",
                  display: "flex",
                  color: "#0958d9",
                  background: "#e6f4ff",
                  border: "1px solid #91caff",
                  fontSize: "12px",
                }}
              >
                <span>{item.name}</span>
                <span
                  style={{
                    marginLeft: "12px",
                  }}
                >
                  <EditOutlined
                    onClick={() => setSelectedSubCollectionId(item._id)}
                  />
                </span>
                <span
                  style={{
                    marginLeft: "4px",
                  }}
                >
                  <Popconfirm
                    title="Delete the sub collection"
                    description="Are you sure to delete this sub collection ?"
                    onConfirm={() => handleDeleteSubCollection(item._id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <CloseOutlined />
                  </Popconfirm>
                </span>
              </div>
            )}
          />
        </div>
      </div>

      <Modal
        title="Edit Sub Collection"
        open={!!selectedSubCollectionId}
        onOk={handleUpdateSubCollection}
        onCancel={closeModal}
        // onClose={closeModal}
        okButtonProps={{
          loading: editLoading,
          disabled: editLoading,
        }}
      >
        <LabelInputComponent
          title="Sub Collection Name"
          required
          labelStyle={{
            color: "#557EFF",
            fontSize: "14px",
            fontWeight: "500",
          }}
          value={subCollectionText}
          onChange={(e: any) => setSubCollectionText(e.target.value)}
        />
      </Modal>
    </Drawer>
  );
};

export default SubCollectionDrawer;
